import React, { useEffect, useRef, useState } from 'react';
import { Property } from '../../services/PropertyService';
import { Imovel } from '../../types/Property';
import { GOOGLE_MAPS_CONFIG, MAP_OPTIONS } from '../../config/maps';
import { geocodingService, GeocodingResult } from '../../services/GeocodingService';
import './MapPanel.css';

interface MapPanelProps {
  properties?: (Property | Imovel)[];
  searchResults?: (Property | Imovel)[];
  zoomLevel?: number;
  center?: { lat: number; lng: number };
}

export const MapPanel: React.FC<MapPanelProps> = ({ properties = [], searchResults = [], zoomLevel, center }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const addMarkersTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Coordenadas de Balneário Camboriú - SC
  const defaultCenter = {
    lat: -26.9906,
    lng: -48.6348
  };

  // Função para construir URL da imagem usando o mesmo padrão da área administrativa
  const getImageUrl = (midiaUrl: string) => {
    if (!midiaUrl) return '';

    // Determinar a base da API a partir das envs
    const rawBackend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '') || 'http://localhost:3005';
    const rawApiBaseEnv = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
    const apiBase = rawApiBaseEnv || (rawBackend.endsWith('/api') ? rawBackend : `${rawBackend}/api`);

    // Extrair origin e path da base da API
    let origin = '';
    let apiPath = '/api';
    try {
      const u = new URL(apiBase);
      origin = u.origin;
      apiPath = u.pathname.replace(/\/+$/, '') || '/api';
    } catch {
      origin = '';
      apiPath = '/api';
    }

    // Normalizar caminho de mídia para sempre usar /uploads a partir do início
    const rawPath = midiaUrl.startsWith('/') ? midiaUrl : `/${midiaUrl}`;
    const idx = rawPath.toLowerCase().indexOf('/uploads');
    const uploadsPath = idx >= 0 ? rawPath.substring(idx) : rawPath;
    const cleanPath = uploadsPath.replace(/^(\/+)(api\/)+/i, '/');

    const finalUrl = `${origin}${apiPath}${cleanPath}`;
    return finalUrl;
  };

  const isValidCoordinate = (value: any): boolean => {
    if (typeof value !== 'number') return false;
    if (isNaN(value)) return false;
    if (!isFinite(value)) return false;
    return value >= -90 && value <= 90;
  };

  const isValidLongitude = (value: any): boolean => {
    if (typeof value !== 'number') return false;
    if (isNaN(value)) return false;
    if (!isFinite(value)) return false;
    return value >= -180 && value <= 180;
  };

  const geocodeWithMapsApi = async (fullAddress: string): Promise<{ lat: number; lng: number } | null> => {
    try {
      if (!fullAddress || fullAddress.trim().length === 0) {
        return null;
      }

      if (window.google?.maps) {
        try {
          const geocoder = new window.google.maps.Geocoder();
          const result: { lat: number; lng: number } | null = await new Promise<{ lat: number; lng: number } | null>((resolve) => {
            const timeout = setTimeout(() => {
              resolve(null);
            }, 5000);

            geocoder.geocode(
              { 
                address: fullAddress.trim(), 
                region: GOOGLE_MAPS_CONFIG.region, 
                language: GOOGLE_MAPS_CONFIG.language 
              },
              (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
                clearTimeout(timeout);
                
                if (status === window.google.maps.GeocoderStatus.OK && results && results.length > 0) {
                  const location = results[0].geometry.location;
                  const lat = location.lat();
                  const lng = location.lng();
                  
                  if (isValidCoordinate(lat) && isValidLongitude(lng)) {
                    resolve({ lat, lng });
                  } else {
                    resolve(null);
                  }
                } else {
                  resolve(null);
                }
              }
            );
          });
          
          if (result) {
            return result;
          }
        } catch (jsError) {
          console.warn('Erro na API JS de Geocoding, tentando HTTP:', jsError);
        }
      }

      const httpResult = await geocodingService.geocodeAddress(fullAddress.trim());
      if (httpResult && isValidCoordinate(httpResult.latitude) && isValidLongitude(httpResult.longitude)) {
        return { lat: httpResult.latitude, lng: httpResult.longitude };
      }

      return null;
    } catch (e) {
      return null;
    }
  };

  const geocodePropertyRobust = async (property: Property | Imovel): Promise<{ lat: number; lng: number }> => {
    const lat = (property as any).latitude;
    const lng = (property as any).longitude;
    
    if (isValidCoordinate(lat) && isValidLongitude(lng)) {
      return { lat, lng };
    }

    const bairro = (property as any).bairro || '';
    
    const addressVariations = [
      [property.endereco, bairro, property.cidade, property.estado, property.cep, 'Brasil'].filter(Boolean).join(', '),
      [property.endereco, bairro, property.cidade, property.estado, 'Brasil'].filter(Boolean).join(', '),
      [property.endereco, property.cidade, property.estado, property.cep, 'Brasil'].filter(Boolean).join(', '),
      [property.endereco, property.cidade, property.estado, 'Brasil'].filter(Boolean).join(', '),
      [bairro, property.cidade, property.estado, property.cep, 'Brasil'].filter(Boolean).join(', '),
      [bairro, property.cidade, property.estado, 'Brasil'].filter(Boolean).join(', '),
      [property.cidade, property.estado, property.cep, 'Brasil'].filter(Boolean).join(', '),
      [property.cidade, property.estado, 'Brasil'].filter(Boolean).join(', ')
    ];

    for (const address of addressVariations) {
      if (!address || address.trim().length === 0) continue;
      
      const coords = await geocodeWithMapsApi(address);
      if (coords && isValidCoordinate(coords.lat) && isValidLongitude(coords.lng)) {
        return coords;
      }
    }

    return { lat: defaultCenter.lat, lng: defaultCenter.lng };
  };

  const addPropertyMarkers = async (map: google.maps.Map) => {
    if (!window.google?.maps) {
      return;
    }

    if (markersRef.current) {
      markersRef.current.forEach(marker => {
        marker.setMap(null);
        window.google.maps.event.clearInstanceListeners(marker);
      });
      markersRef.current = [];
    }
    
    const propertiesToShow = searchResults.length > 0 ? searchResults : properties;

    if (propertiesToShow.length === 0) {
      map.setCenter(defaultCenter);
      map.setZoom(14);
      return;
    }

    if (propertiesToShow.length === 1) {
      const property = propertiesToShow[0];
      const coords = await geocodePropertyRobust(property);

      if (!window.google?.maps) {
        return;
      }

      const marker = new window.google.maps.Marker({
        position: { lat: coords.lat, lng: coords.lng },
        map: map,
        title: property.titulo || '',
        animation: window.google.maps.Animation.DROP
      });

      markersRef.current.push(marker);
      map.setCenter({ lat: coords.lat, lng: coords.lng });
      map.setZoom(17);
      return;
    }

    const validCoordinates: Array<{ property: Property | Imovel; lat: number; lng: number }> = [];

    const uniqueProperties = propertiesToShow.filter((property, index, self) => 
      index === self.findIndex(p => p.id === property.id)
    );

    for (const property of uniqueProperties) {
      const coords = await geocodePropertyRobust(property);
      validCoordinates.push({ property, lat: coords.lat, lng: coords.lng });
    }

    if (markersRef.current) {
      markersRef.current.forEach(marker => {
        marker.setMap(null);
        if (window.google?.maps) {
          window.google.maps.event.clearInstanceListeners(marker);
        }
      });
      markersRef.current = [];
    }

    if (!window.google?.maps) {
      return;
    }

    validCoordinates.forEach(({ property, lat, lng }) => {
      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map: map,
        title: '',
        animation: window.google.maps.Animation.DROP
      });

      markersRef.current.push(marker);
    });

    if (validCoordinates.length > 0 && window.google?.maps) {
      const bounds = new window.google.maps.LatLngBounds();
      validCoordinates.forEach(({ lat, lng }) => {
        bounds.extend(new window.google.maps.LatLng(lat, lng));
      });
      
      if (validCoordinates.length === 1) {
        map.setCenter({ lat: validCoordinates[0].lat, lng: validCoordinates[0].lng });
        map.setZoom(17);
      } else {
        map.fitBounds(bounds);
        
        setTimeout(() => {
          const currentZoom = map.getZoom();
          if (typeof currentZoom === 'number') {
            if (currentZoom > 18) {
              map.setZoom(18);
            } else if (currentZoom < 12) {
              map.setZoom(12);
            }
          }
        }, 100);
      }
    }
  };

  const isGoogleMapsReady = (): boolean => {
    return !!(
      window.google &&
      window.google.maps &&
      typeof window.google.maps.Map === 'function' &&
      typeof window.google.maps.Marker === 'function' &&
      typeof window.google.maps.LatLng === 'function' &&
      typeof window.google.maps.LatLngBounds === 'function'
    );
  };

  useEffect(() => {
    const loadGoogleMaps = () => {
      if (isGoogleMapsReady()) {
        setTimeout(() => initializeMap(), 50);
        return;
      }

      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        let attempts = 0;
        const maxAttempts = 50;
        const checkLoaded = () => {
          attempts++;
          if (isGoogleMapsReady()) {
            setTimeout(() => initializeMap(), 50);
          } else if (attempts < maxAttempts) {
            setTimeout(checkLoaded, 100);
          } else {
            setIsLoading(false);
            setError('Timeout ao carregar Google Maps. Verifique sua conexão e API Key.');
          }
        };
        checkLoaded();
        return;
      }

      if (!GOOGLE_MAPS_CONFIG.apiKey) {
        setIsLoading(false);
        setError('API Key do Google Maps não configurada. Configure REACT_APP_GOOGLE_MAPS_API_KEY.');
        return;
      }

      const script = document.createElement('script');
      const apiKey = GOOGLE_MAPS_CONFIG.apiKey;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${GOOGLE_MAPS_CONFIG.libraries.join(',')}&region=${GOOGLE_MAPS_CONFIG.region}&language=${GOOGLE_MAPS_CONFIG.language}&loading=async`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        let attempts = 0;
        const maxAttempts = 50;
        const checkReady = () => {
          attempts++;
          if (isGoogleMapsReady()) {
            setIsLoaded(true);
            setIsLoading(false);
            setError(null);
            setTimeout(() => initializeMap(), 50);
          } else if (attempts < maxAttempts) {
            setTimeout(checkReady, 100);
          } else {
            setIsLoading(false);
            setError('Google Maps carregado mas construtores não disponíveis. Verifique a API Key e se as APIs estão ativadas.');
          }
        };
        checkReady();
      };
      
      script.onerror = (error) => {
        setIsLoading(false);
        setError('Erro ao carregar o script do Google Maps. Verifique sua API Key e se as APIs estão ativadas no Google Cloud Console.');
      };
      
      document.head.appendChild(script);
    };

    const initializeMap = () => {
      if (!mapRef.current) {
        console.error('MapPanel: mapRef.current é null');
        setIsLoading(false);
        setError('Erro: elemento do mapa não encontrado.');
        return;
      }

      if (!isGoogleMapsReady()) {
        console.error('MapPanel: Google Maps não está totalmente carregado');
        setIsLoading(false);
        setError('Google Maps API não está totalmente carregada. Aguarde alguns instantes e recarregue a página.');
        return;
      }

      if (!mapRef.current.parentNode) {
        console.error('MapPanel: elemento do mapa não está no DOM');
        setIsLoading(false);
        setError('Erro: elemento do mapa removido do DOM.');
        return;
      }

      try {
        if (typeof window.google.maps.Map !== 'function') {
          throw new Error('window.google.maps.Map não é um construtor');
        }

        const mapOptions: google.maps.MapOptions = {
          ...MAP_OPTIONS,
          zoom: zoomLevel || MAP_OPTIONS.zoom,
          center: center || MAP_OPTIONS.center
        };
        
        const map = new window.google.maps.Map(mapRef.current, mapOptions);

        if (!map) {
          throw new Error('Falha ao criar instância do mapa');
        }

        mapInstanceRef.current = map;
        setIsLoading(false);
        setError(null);

        addPropertyMarkers(map).catch(error => {
          console.error('Erro ao adicionar marcadores:', error);
        });
      } catch (error: any) {
        console.error('Erro ao inicializar o mapa:', error);
        setIsLoading(false);
        const errorMessage = error?.message || 'Erro desconhecido';
        setError(`Erro ao inicializar o mapa: ${errorMessage}. Verifique sua API Key e se as APIs (Maps JavaScript API, Geocoding API) estão ativadas no Google Cloud Console.`);
      }
    };


    loadGoogleMaps();

    return () => {
      markersRef.current.forEach(marker => marker.setMap(null));
    };
  }, [properties]);

  useEffect(() => {
    const propertiesToShow = searchResults.length > 0 ? searchResults : properties;
    if (propertiesToShow.length === 0) {
      return;
    }
    
    if (mapInstanceRef.current && isLoaded) {
      if (addMarkersTimeoutRef.current) {
        clearTimeout(addMarkersTimeoutRef.current);
      }
      
      const currentMap = mapInstanceRef.current;
      addMarkersTimeoutRef.current = setTimeout(() => {
        if (currentMap) {
          addPropertyMarkers(currentMap).catch(error => {
            console.error('Erro ao atualizar marcadores:', error);
          });
        }
      }, 300);
    }
    
    return () => {
      if (addMarkersTimeoutRef.current) {
        clearTimeout(addMarkersTimeoutRef.current);
      }
    };
  }, [properties, searchResults, isLoaded]);

  const handleZoomIn = () => {
    if (mapInstanceRef.current) {
      const currentZoom = mapInstanceRef.current.getZoom();
      if (currentZoom !== undefined && typeof currentZoom === 'number') {
        mapInstanceRef.current.setZoom(currentZoom + 1);
      }
    }
  };

  const handleZoomOut = () => {
    if (mapInstanceRef.current) {
      const currentZoom = mapInstanceRef.current.getZoom();
      if (currentZoom !== undefined && typeof currentZoom === 'number') {
        mapInstanceRef.current.setZoom(currentZoom - 1);
      }
    }
  };

  return (
    <div className="map-panel">
      <div className="map-container">
        {isLoading && (
          <div className="map-loading">
            <div className="loading-spinner"></div>
            <p>Carregando mapa...</p>
          </div>
        )}
        
        {error && (
          <div className="map-error">
            <div className="error-icon"></div>
            <p>{error}</p>
            <button 
              className="retry-btn" 
              onClick={() => {
                setIsLoading(true);
                setError(null);
                window.location.reload();
              }}
            >
              Tentar Novamente
            </button>
          </div>
        )}
        
        <div 
          ref={mapRef} 
          className="google-map"
          style={{ display: isLoading || error ? 'none' : 'block' }}
        />
        
        {!isLoading && !error && (
          <>
            <div className="map-controls">
              <button className="control-btn zoom-in" onClick={handleZoomIn}>+</button>
              <button className="control-btn zoom-out" onClick={handleZoomOut}>−</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};